import type { EventTuple } from './types'
import type { VoxelMap } from '@/voxelTypes'
import type { SnapshotMeta } from '@/document/model'
import { applyWithRegistry } from './eventRegistry'
import { setActiveMirrorCtx } from './layerSystem'
import { timeFunctionMulti } from '@/perf/log'
import { toDocumentBase } from '@/storage/grid/documentBase'
import { isChunkedVoxelGrid } from '@/storage/grid/ChunkedVoxelGrid'
import { diffGrids, forEachCellChange } from '@/storage/grid/gridDiff'
import {
  setActiveMutationCandidateCollector,
  type VoxelMutationJournal,
} from '@/events/mutationJournal'

export type ReducedState = { base: VoxelMap; ui: any; meta: SnapshotMeta }
export type ReduceEventsOptions = {
  mutationJournal?: VoxelMutationJournal
  /** The document before the open draft (`HandlerCtx.strokeBase`), when the events belong to one. */
  strokeBase?: VoxelMap
}

/**
 * Core reducer: apply events over { base, ui } in the context of a Document.
 * Does not depend on Jotai; atoms reuse this to compose preview state.
 */
export function reduceEvents(initial: ReducedState, events: EventTuple[], options: ReduceEventsOptions = {}): ReducedState {
  if (events.length == 0) return initial
  const journal = options.mutationJournal
  // Chunk-delta journal (plan §3.2): on a grid base no candidate collector is installed, so
  // `recordMutationCandidate` is a null check per visited cell; `finalize` diffs chunk refs instead.
  const chunkJournal = !!journal && isChunkedVoxelGrid(initial.base)
  if (chunkJournal) journal!.beginChunkDiff()
  const installCollector = !!journal && !chunkJournal
  const previousCollector = installCollector ? setActiveMutationCandidateCollector(journal!) : null
  const ctx: any = {}
  if (options.strokeBase) ctx.strokeBase = options.strokeBase
  // Stage UI locally, mutate by setUi aggregator
  let stagedUi = initial.ui
  // Ensure handlers can read current UI immediately
  ctx.ui = stagedUi
  // IMPORTANT: Accumulate UI patches across the batch.
  // Use the latest ctx.ui as the previous value for each successive set,
  // not the initial stagedUi snapshot, otherwise earlier ui.set calls get dropped.
  ctx.setUi = (updater: ((prev: any) => any) | Record<string, any>) => {
    const prev = ctx.ui
    ctx.ui = typeof updater === 'function' ? (updater as any)(prev) : { ...prev, ...(updater as any) }
  }
  
  // Stage Meta locally, mutate by setMeta aggregator
  let stagedMeta: SnapshotMeta = (initial as any).meta
  ctx.meta = stagedMeta
  // Accumulate Meta patches within the batch as well
  ctx.setMeta = (updater: ((prev: any) => any) | Record<string, any>) => {
    const prev = ctx.meta
    ctx.meta = typeof updater === 'function' ? (updater as any)(prev) : { ...prev, ...(updater as any) }
  }
  

  // Base map handling
  // Start from the durable base. On first document-affecting event, switch to a transient builder:
  // - If base supports transients (a ChunkedVoxelGrid, the document base), use
  //   asMutable()/asImmutable() to avoid a full copy (a grid transient is an O(1) copy of 64 top refs)
  // - If base is a native Map (sparse layers, previews, tests), fall back to a shallow clone
  const isTransientCapable = (m: any) => m && typeof m.asMutable === 'function' && typeof m.asImmutable === 'function'
  let usingTransient = isTransientCapable(initial.base)
  ctx.base = initial.base
  let mutated = false
  const ensureClone = () => {
    if (mutated) return
    if (usingTransient) {
      ctx.base = (initial.base as any).asMutable()
    } else {
      ctx.base = new Map<number, any>(initial.base as any)
    }
    mutated = true
  }

  // Replace base efficiently (used by checkpoints and whole-base rewrites such as geom.translate)
  ctx.setBase = (next: VoxelMap) => {
    const prev: VoxelMap = ctx.base
    // Normalize to the durable representation; a grid is rebased onto the current lineage palette.
    const currentPalette = isChunkedVoxelGrid(prev) ? prev.palette : undefined
    const normalized = toDocumentBase(next, currentPalette, ctx.meta?.bounds)
    if (journal && journal.collectsCandidates) {
      // Candidate journal only (the chunk journal diffs the whole before/after grids in finalize).
      // When both bases are grids on one palette the candidates come from a chunk-ref diff instead
      // of every key of both bases.
      const deltas = isChunkedVoxelGrid(prev) && isChunkedVoxelGrid(normalized) ? diffGrids(prev, normalized) : 'incomparable'
      if (deltas !== 'incomparable') {
        forEachCellChange(deltas, (key) => journal.addCandidate(key))
      } else {
        ;(prev as any)?.forEach?.((_: unknown, key: number) => journal.addCandidate(key))
        ;(normalized as any)?.forEach?.((_: unknown, key: number) => journal.addCandidate(key))
      }
    }
    // Continue the current mutation mode if any
    ctx.base = mutated ? (normalized as any).asMutable?.() ?? normalized : normalized
    usingTransient = true
    mutated = true
  }

  // Queue allows handlers to dispatch nested events
  const queue: EventTuple[] = []
  ctx.dispatchEvents = (more: EventTuple[]) => { for (const e of more) queue.push(e) }
  ctx.set = () => { }

  const processOne = (ev: EventTuple) => {
    const type = String(ev[0])
    if (!type.startsWith('ui.')) ensureClone()
    // Activate mirror context for this event (fast path inside writeFn keeps perf when none)
    try {
      setActiveMirrorCtx({ bounds: ctx.meta.bounds, mirrors: (ctx.meta as any).mirrors || {} })
      timeFunctionMulti(`event[${type}]`, () => applyWithRegistry(ctx as any, ev))
    } finally {
      setActiveMirrorCtx(null)
    }
  }

  try {
    for (const ev of events) processOne(ev)
    while (queue.length) processOne(queue.shift() as EventTuple)
  } finally {
    if (installCollector) setActiveMutationCandidateCollector(previousCollector)
  }

  // Finalize transient mutation
  let outBase: VoxelMap = ctx.base
  if (mutated && usingTransient && ctx.base && typeof (ctx.base as any).asImmutable === 'function') {
    outBase = (ctx.base as any).asImmutable()
  }
  const reduced = { base: outBase, ui: ctx.ui, meta: ctx.meta }
  journal?.finalize(initial.base, outBase)
  return reduced
}
